import * as React from "react";
import { Key, Mail } from "lucide-react";
import { useAction } from "convex/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ENV } from "@/env";
import { api } from "$convex/convexApi";
import { copyTextToClipboard } from "@/lib/clipboard";

interface PasswordResetButtonProps
  extends Omit<React.ComponentProps<typeof Button>, "children" | "onClick"> {
  email: string | null | undefined;
  label?: string;
  busyLabel?: string;
}

export function PasswordResetEmailButton({
  email,
  label = "Send Email",
  busyLabel = "Sending...",
  disabled,
  ...buttonProps
}: PasswordResetButtonProps) {
  const [sending, setSending] = React.useState(false);
  const sendPasswordResetEmail = useAction(api.salesActions.sendPasswordResetEmail);
  const targetEmail = email?.trim() ?? "";

  const handleSend = React.useCallback(async () => {
    if (!targetEmail) {
      toast.error("Select a user with an email first");
      return;
    }

    setSending(true);
    try {
      await sendPasswordResetEmail({
        targetEmail,
        siteUrl: ENV.SITE_URL || undefined,
      });
      toast.success(`Password reset email sent to ${targetEmail}`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to send password reset email",
      );
    } finally {
      setSending(false);
    }
  }, [sendPasswordResetEmail, targetEmail]);

  return (
    <Button
      type="button"
      disabled={disabled || sending || !targetEmail}
      onClick={() => void handleSend()}
      {...buttonProps}
    >
      <Mail className="mr-2 h-4 w-4" />
      {sending ? busyLabel : label}
    </Button>
  );
}

export function PasswordResetCopyLinkButton({
  email,
  label = "Copy 7d Link",
  busyLabel = "Creating...",
  disabled,
  ...buttonProps
}: PasswordResetButtonProps) {
  const [generating, setGenerating] = React.useState(false);
  const generatePasswordResetLink = useAction(api.salesActions.generatePasswordResetLink);
  const targetEmail = email?.trim() ?? "";

  const handleCopy = React.useCallback(async () => {
    if (!targetEmail) {
      toast.error("Select a user with an email first");
      return;
    }

    setGenerating(true);
    try {
      const result = await generatePasswordResetLink({
        targetEmail,
        siteUrl: ENV.SITE_URL || undefined,
      });

      const expiresLabel = new Date(result.expiresAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      try {
        await copyTextToClipboard(result.resetUrl);
      } catch {
        if (typeof window !== "undefined") {
          window.prompt("Copy password reset link", result.resetUrl);
        }
      }

      toast.success(`7-day reset link copied for ${targetEmail}. Expires ${expiresLabel}.`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate password reset link",
      );
    } finally {
      setGenerating(false);
    }
  }, [generatePasswordResetLink, targetEmail]);

  return (
    <Button
      type="button"
      disabled={disabled || generating || !targetEmail}
      onClick={() => void handleCopy()}
      {...buttonProps}
    >
      <Key className="mr-2 h-4 w-4" />
      {generating ? busyLabel : label}
    </Button>
  );
}
